import { useCallback, useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { AccordionFieldsetProps, ViberIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getDocsUrl } from '@/util/constants';
import { errorMessage, trimOnBlur } from '@/util/validation/helpers';
import { integrationRulesViber } from '@/util/validation/rules/integration-rules';

type Form = { access_token: string; name: string };

const ViberConnect = ({
  type,
  integration,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, ViberIntegration>) => {
  const { t } = useTranslation();
  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const { updateIntegration, updateStatus } = useIntegrations<ViberIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );
  const dispatch = useDispatch();

  const formMethods = useForm<Form>({ mode: 'onSubmit' });

  const {
    control,
    reset,
    formState: { errors, isDirty },
    handleSubmit,
    getValues,
    trigger,
  } = formMethods;

  useEffect(() => {
    reset({
      access_token: integration?.config?.access_token || '',
      name: integration?.config?.name || '',
    });
  }, [integration?.config?.name, integration?.config?.access_token, reset]);

  const handleUpdateIntegration = useCallback(() => {
    const { access_token, name } = getValues();
    updateIntegration(
      { ...integration, config: { ...integration.config, access_token, name } },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [dispatch, expanded, getValues, integration, type, updateIntegration]);

  usePrompt(isDirty, undefined, undefined, handleSubmit(handleUpdateIntegration));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  // for reseting changes
  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [reset, expanded, isDirty, type]);

  return (
    <>
      <Accordion
        title={t('integrations.viber_connect.title')}
        subtitle={t('integrations.viber_connect.subtitle')}
        footerText={t('integrations.viber_connect.footer_text')}
        onSubmit={handleSubmit(handleUpdateIntegration)}
        disabled={!isDirty}
        isLoading={updateStatus === 'pending'}
        expanded={expanded === type}
        handleChange={toggleAccordion(type)}
        ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
        readOnly={!canWrite}
      >
        <TitleSubtitle
          title={t('integrations.viber_connect.access_token.title')}
          subtitle={t('integrations.viber_connect.access_token.subtitle')}
          externalLink={{
            label: t('integrations.viber_connect.access_token.external_link'),
            url: getDocsUrl('/docs/integrations/viber'),
          }}
          error={!!errors.access_token}
        />
        <div className="pb-4">
          <Controller
            control={control}
            name="access_token"
            rules={{
              required: { value: true, message: t('validation.required') },
              maxLength: { value: 64, message: t('validation.max_length', { 0: 64 }) },
            }}
            render={({ field }) => (
              <Input
                id="access_token"
                placeholder={t('integrations.viber_connect.access_token.placeholder')}
                aria-invalid={!!errors.access_token}
                readOnly={!canWrite}
                {...field}
                value={field.value ?? ''}
                onBlur={trimOnBlur(field)}
              />
            )}
          />
          {errorMessage({
            field: errors.access_token,
            maxLength: integrationRulesViber.accessToken.maxLength,
          }) && <p className="mt-1 text-sm text-warning">{errors.access_token?.message}</p>}
        </div>

        <TitleSubtitle
          title={t('integrations.viber_connect.name.title')}
          subtitle={t('integrations.viber_connect.name.subtitle')}
          error={!!errors.name}
        />
        <div className="pb-3">
          <Controller
            control={control}
            name="name"
            rules={{
              required: { value: true, message: t('validation.required') },
              maxLength: { value: 28, message: t('validation.max_length', { 0: 28 }) },
            }}
            render={({ field }) => (
              <Input
                id="name"
                placeholder={t('integrations.viber_connect.name.placeholder')}
                aria-invalid={!!errors.name}
                readOnly={!canWrite}
                {...field}
                value={field.value ?? ''}
                onBlur={trimOnBlur(field)}
              />
            )}
          />
          {errorMessage({
            field: errors.name,
            maxLength: integrationRulesViber.name.maxLength,
          }) && <p className="mt-1 text-sm text-warning">{errors.name?.message}</p>}
        </div>
      </Accordion>
    </>
  );
};

export default ViberConnect;
